import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faClose, faMapLocationDot, faMotorcycle, faRoute, faRotateRight } from "@fortawesome/free-solid-svg-icons"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { ButtonIcon } from "../../../components/Button/Button"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { DeliveryRoutePlan } from "../../../Objects/DeliveryEntity/DeliveryRoutePlan"
import { useDeliveryRoutePlan } from "../../../hooks/Delivery/useDeliveryRoutePlan"
import { openMultiStopGoogleMaps } from "../../../hooks/Delivery/deliveryListUtils"
import { CourierRoutePlanBody } from "../Components/CourierRoutePlanBody"

interface Props {
  courierId: string
  courierName: string
  onClose: () => void
}

export const CourierActiveRoutePupop = ({ courierId, courierName, onClose }: Props) => {
  const { t } = useTranslation("frontend")

  const { isLoadingRoutePlan, handleGetCourierRoutePlanForRestaurant } = useDeliveryRoutePlan()

  const [plan, setPlan] = useState<DeliveryRoutePlan | null>(null)

  const [selectedOptionKey, setSelectedOptionKey] = useState<string>()

  const [expandedStopKey, setExpandedStopKey] = useState<string>()

  const fetchPlan = () => {
    handleGetCourierRoutePlanForRestaurant(courierId, (nextPlan) => {
      setPlan(nextPlan)

      setSelectedOptionKey((current) => {
        if (current && nextPlan.options.some((option) => option.key === current)) {
          return current
        }

        return nextPlan.recommendedOptionKey ?? nextPlan.options[0]?.key
      })
    })
  }

  useEffect(() => {
    fetchPlan()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courierId])

  const selectedOption = plan?.options.find((option) => option.key === selectedOptionKey) ?? plan?.options[0]

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert2}>
            <TitlePageResponsiveIconSmall
              title={t("courier_partners.active_route.title", { name: courierName })}
              icon={faMotorcycle}
              boxShadow="none"
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className="flex items-center gap-2">
                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor="#ffffff"
                    icon={faRotateRight}
                    onClick={fetchPlan}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.delivery.pupop.courierActiveRoutePupop.button.fetchPlan"
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.delivery.pupop.courierActiveRoutePupop.button.onClose"
                  />
                </div>
              }
            />

            <div className="space-y-4 overflow-y-auto p-2">
              <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
                <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 text-sm text-slate-700">
                  {t("courier_partners.active_route.scope_notice")}
                </div>
              </Animate>

              {isLoadingRoutePlan && !plan && (
                <div className="flex flex-col items-center justify-center gap-3 py-16 text-slate-400">
                  <FontAwesomeIcon icon={faRotateRight} spin className="text-2xl" />
                  <p className="text-sm font-semibold">{t("route_plan.loading")}</p>
                </div>
              )}

              {!isLoadingRoutePlan && (!plan || plan.options.length === 0) && (
                <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-400">
                  <FontAwesomeIcon icon={faRoute} className="text-2xl" />
                  <p className="text-sm font-semibold">{t("courier_partners.active_route.empty")}</p>
                </div>
              )}

              {plan && plan.options.length > 0 && (
                <>
                  <CourierRoutePlanBody
                    plan={plan}
                    selectedOptionKey={selectedOptionKey}
                    onSelectOption={(key) => {
                      setSelectedOptionKey(key)
                      setExpandedStopKey(undefined)
                    }}
                    expandedStopKey={expandedStopKey}
                    onToggleStop={(key) => setExpandedStopKey((current) => (current === key ? undefined : key))}
                  />

                  {selectedOption && selectedOption.stops.length > 0 && (
                    <button
                      type="button"
                      onClick={() => openMultiStopGoogleMaps(selectedOption)}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 py-3 text-sm font-black text-white shadow-md transition hover:opacity-90"
                      data-interaction-description-key="interactionDescriptions:pages.delivery.pupop.courierActiveRoutePupop.button.openGoogleMaps"
                    >
                      <FontAwesomeIcon icon={faMapLocationDot} />

                      {t("route_plan.open_in_maps")}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
